/**
 * Gallery — everything you've made, with the exact request behind it.
 *
 * The detail panel is the honest part: the literal prompt that was sent, the
 * inputs it used, the parameters, what it cost and how long it took. That's what
 * makes a good render repeatable instead of lucky.
 */
import { useState } from 'react';
import { downloadMedia, type Render } from '../lib/api';
import { fileBase, money } from '../lib/compose';
import Img from '../components/Img';
import DownloadButton from '../components/DownloadButton';

export interface GalleryProps {
  renders: Render[];
  onReuse: (render: Render) => void;
}

export default function Gallery({ renders, onReuse }: GalleryProps) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const open = renders.find((r) => r.id === openId) ?? null;

  async function save(render: Render) {
    if (!render.src) return;
    setError(null);
    try {
      await downloadMedia(render.src, fileBase(render.prompt, render.id));
    } catch {
      setError('Could not save that image');
    }
  }

  return (
    <div className="pad">
      <div className="page-head">
        <div>
          <h2>Gallery</h2>
          <p>Everything you've made, with the request behind it. Anything here can go back in as an input.</p>
        </div>
      </div>

      {renders.length === 0 ? (
        <div className="notice">Nothing yet. Renders land here once they finish.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(178px, 1fr))', gap: 13 }}>
          {renders.map((r) => (
            <button
              key={r.id} className="card" style={{ padding: 0, overflow: 'hidden', textAlign: 'left' }}
              onClick={() => { setError(null); setOpenId(r.id); }}
            >
              <Img src={r.src} alt="Render" style={{ width: '100%', aspectRatio: '3/4', borderRadius: 0 }} />
              <span style={{ display: 'flex', gap: 7, padding: '9px 11px 11px', alignItems: 'center' }}>
                <span className="mono" style={{ fontSize: 11 }}>{r.resolution.toUpperCase()}</span>
                <span className="mono faint" style={{ fontSize: 10, marginLeft: 'auto' }}>
                  {money(r.costUsd)}{r.tookMs ? ` · ${(r.tookMs / 1000).toFixed(1)}s` : ''}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}

      {open && (
        <>
          <div className="scrim" onClick={() => setOpenId(null)} />
          <aside className="modal" style={{ width: 'min(460px, 94vw)', top: 0, right: 0, left: 'auto', bottom: 0, transform: 'none', maxHeight: '100vh', borderRadius: 0 }}>
            <div className="modal-head">
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13.5 }}>Render</div>
                <div className="mono faint" style={{ fontSize: 10.5 }}>{open.predictionId ?? open.id}</div>
              </div>
              <DownloadButton title="Download this image" onClick={() => void save(open)} />
              <button className="btn btn-sm" onClick={() => { onReuse(open); setOpenId(null); }}>Use as input</button>
              <button className="btn btn-sm" onClick={() => setOpenId(null)}>Close</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {error && <div className="notice bad" role="alert">{error}</div>}
              <Img src={open.src} alt="Render" style={{ width: '100%', aspectRatio: '3/4' }} />

              <div>
                <span className="eyebrow">Inputs it used</span>
                <div style={{ display: 'flex', gap: 7, marginTop: 9, flexWrap: 'wrap' }}>
                  {open.inputs.map((i, n) => (
                    <div key={n} style={{ width: 62 }}>
                      <Img src={i.src} alt={i.label} style={{ width: '100%', aspectRatio: '1' }} />
                      <div className="mono faint" style={{ fontSize: 9.5, marginTop: 4 }}>{n + 1} · {i.role}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <span className="eyebrow">The request</span>
                <pre className="req" style={{ marginTop: 9 }}>
                  <span className="k">prompt</span>  {open.prompt}{'\n\n'}
                  <span className="k">images</span>  {open.inputs.length}{'\n'}
                  <span className="k">shape</span>   {open.aspect === 'auto' ? 'auto (from image 1)' : open.aspect}{'\n'}
                  <span className="k">size</span>    {open.resolution}{'\n'}
                  <span className="k">file</span>    {open.format}{'\n\n'}
                  <span className="k">cost</span>    <span className="v">{money(open.costUsd)}</span>{'\n'}
                  <span className="k">took</span>    <span className="v">{open.tookMs ? `${(open.tookMs / 1000).toFixed(1)}s` : '—'}</span>
                </pre>
              </div>
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
